import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrders } from '../contexts/OrderContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/StatusBadge';
import { DeliveryConfirmationModal } from '../components/DeliveryConfirmationModal';
import { ArrowLeft, MapPin, Phone, Package, CheckCircle, Truck, AlertTriangle } from 'lucide-react';
import { storage } from '../utils/storage';
export function DriverTourDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    tours,
    orders,
    updateOrderStatus,
    refreshData,
    markOrderAsReturn
  } = useOrders();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const tour = tours.find(t => t.id === id);
  if (!tour) {
    return <div className="p-8 text-center" data-id="element-453">Tournée introuvable</div>;
  }
  const tourOrders = orders.filter(o => tour.orderIds.includes(o.id));
  const isCompleted = tour.status === 'TERMINEE';
  // Check if all orders are processed to allow finishing tour
  const allOrdersProcessed = tourOrders.every(o => tour.type === 'LIVRAISON' && (o.status === 'LIVREE' || o.status === 'RETOUR_DEPOT') || tour.type === 'COLLECTE' && o.status === 'COLLECTEE');
  const handleDeliveryConfirm = async (signature: string, comments: string) => {
    if (selectedOrderId) {
      await updateOrderStatus(selectedOrderId, 'LIVREE', `Livraison confirmée. Reçu par: ${signature}. Note: ${comments}`);
      setSelectedOrderId(null);
    }
  };
  const handleReturnToDepot = async (orderId: string) => {
    const reason = prompt('Raison du retour (client absent, adresse incorrecte, refus, etc.):');
    if (reason) {
      await markOrderAsReturn(orderId, reason);
      alert('Commande marquée comme retour. Elle sera automatiquement ajoutée à la prochaine tournée avec priorité.');
    }
  };
  const handleFinishTour = () => {
    if (window.confirm('Voulez-vous marquer cette tournée comme terminée ?')) {
      const updatedTour = {
        ...tour,
        status: 'TERMINEE' as const
      };
      storage.saveTour(updatedTour);
      refreshData();
      navigate('/driver/tours');
    }
  };
  return <div className="space-y-6 pb-20" data-id="element-454">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-455">
        <div className="flex items-center" data-id="element-456">
          <Button variant="ghost" onClick={() => navigate('/driver/tours')} className="mr-2 pl-0" data-id="element-457">
            <ArrowLeft className="w-5 h-5" data-id="element-458" />
          </Button>
          <div data-id="element-459">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-460">
              Tournée {tour.zone}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-461">
              {new Date(tour.date).toLocaleDateString('fr-FR')} • {tour.vehicle}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-bold rounded ${tour.type === 'LIVRAISON' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-462">
          {tour.type}
        </span>
      </div>

      {/* Priority Alert */}
      {tourOrders.some(o => o.status === 'RETOUR_DEPOT') && <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg" data-id="element-463">
          <div className="flex items-center" data-id="element-464">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" data-id="element-465" />
            <div data-id="element-466">
              <p className="font-medium text-red-800" data-id="element-467">
                Commandes prioritaires (retours)
              </p>
              <p className="text-sm text-red-600" data-id="element-468">
                {tourOrders.filter(o => o.status === 'RETOUR_DEPOT').length}{' '}
                commande(s) en retour - À livrer en priorité
              </p>
            </div>
          </div>
        </div>}

      {/* Orders List */}
      <div className="space-y-4" data-id="element-469">
        {tourOrders.map((order, index) => <Card key={order.id} className={`relative ${order.status === 'LIVREE' ? 'bg-gray-50' : order.status === 'RETOUR_DEPOT' ? 'border-l-4 border-l-red-500 bg-red-50' : 'border-l-4 border-l-blue-500'}`} data-id="element-470">
            <div className="absolute top-4 right-4" data-id="element-471">
              <span className="text-xs font-bold text-gray-400" data-id="element-472">
                #{index + 1}
              </span>
            </div>

            <div className="pr-8" data-id="element-473">
              <div className="flex items-center gap-2 mb-2" data-id="element-474">
                <h3 className="font-bold text-gray-900" data-id="element-475">{order.clientName}</h3>
                {order.status === 'RETOUR_DEPOT' && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded font-bold" data-id="element-476">
                    RETOUR - Priorité
                  </span>}
                {order.deliveryAttempts > 0 && <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded" data-id="element-477">
                    Tentative #{order.deliveryAttempts + 1}
                  </span>}
              </div>
              <div className="mt-1 flex items-center text-gray-600 text-sm" data-id="element-478">
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-479" />
                {order.address}
              </div>
              <div className="mt-1 flex items-center text-gray-600 text-sm" data-id="element-480">
                <Phone className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-481" />
                <a href={`tel:${order.clientPhone}`} className="underline" data-id="element-482">
                  {order.clientPhone}
                </a>
              </div>

              <div className="mt-3 flex items-center justify-between" data-id="element-483">
                <div className="flex items-center text-sm font-medium text-gray-700" data-id="element-484">
                  <Package className="w-4 h-4 mr-2" data-id="element-485" />
                  {order.type} (x{order.quantity})
                </div>
                <StatusBadge status={order.status} data-id="element-486" />
              </div>
            </div>

            {/* Actions */}
            {!isCompleted && <div className="mt-4 pt-4 border-t border-gray-200 space-y-2" data-id="element-487">
                {tour.type === 'LIVRAISON' && order.status !== 'LIVREE' && order.status !== 'RETOUR_DEPOT' && <>
                      <Button fullWidth onClick={() => {
              setSelectedOrderId(order.id);
              setIsModalOpen(true);
            }} className="bg-green-600 hover:bg-green-700" data-id="element-488">
                        Confirmer Livraison
                      </Button>
                      <Button fullWidth variant="danger" onClick={() => handleReturnToDepot(order.id)} className="bg-red-600 hover:bg-red-700 text-white" data-id="element-489">
                        Retour Dépôt (Non livré)
                      </Button>
                    </>}

                {tour.type === 'LIVRAISON' && order.status === 'RETOUR_DEPOT' && <>
                      <Button fullWidth onClick={() => {
              setSelectedOrderId(order.id);
              setIsModalOpen(true);
            }} className="bg-green-600 hover:bg-green-700" data-id="element-490">
                        Confirmer Livraison (Retour)
                      </Button>
                      <Button fullWidth variant="danger" onClick={() => handleReturnToDepot(order.id)} className="bg-red-600 hover:bg-red-700 text-white" data-id="element-491">
                        Nouveau Retour Dépôt
                      </Button>
                    </>}

                {tour.type === 'COLLECTE' && order.status !== 'COLLECTEE' && <Button fullWidth onClick={() => navigate(`/orders/${order.id}/collect`)} className="bg-blue-600 hover:bg-blue-700" data-id="element-492">
                    Confirmer Collecte
                  </Button>}

                {(order.status === 'LIVREE' || order.status === 'COLLECTEE') && <div className="text-center text-sm text-green-600 font-medium flex items-center justify-center" data-id="element-493">
                    <CheckCircle className="w-4 h-4 mr-2" data-id="element-494" />
                    Traité
                  </div>}
              </div>}
          </Card>)}
      </div>

      {/* Footer Action */}
      {!isCompleted && <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg" data-id="element-495">
          <div className="max-w-7xl mx-auto" data-id="element-496">
            <Button fullWidth size="lg" disabled={!allOrdersProcessed} onClick={handleFinishTour} className={allOrdersProcessed ? 'bg-gray-900' : 'bg-gray-300'} data-id="element-497">
              Terminer la Tournée
            </Button>
          </div>
        </div>}

      <DeliveryConfirmationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDeliveryConfirm} clientName={selectedOrderId ? tourOrders.find(o => o.id === selectedOrderId)?.clientName || '' : ''} data-id="element-498" />
    </div>;
}